import { Link } from 'react-router-dom';

import { LegalPage, P, Section, UL } from './LegalPage';

export function DpaPage() {
  return (
    <LegalPage
      title="Data Processing Agreement"
      subtitle="Standard DPA terms for workspaces processing personal data of EU/UK/Swiss residents through Emailforge."
      effectiveDate="April 25, 2026"
    >
      <Section title="Roles">
        <P>
          You ("Customer") act as Controller of personal data you upload, send to, or process through Emailforge.
          Emailforge acts as Processor and processes that data only on your documented instructions, which are the
          Terms of Service, this DPA, and any in-product configuration you set.
        </P>
      </Section>

      <Section title="Categories of data and data subjects">
        <UL>
          <li>
            <strong>Recipients</strong> — typically your customers, leads, or employees. Data: email address (hashed
            for analytics), and any merge variables you supply (name, order details, etc.).
          </li>
          <li>
            <strong>Workspace members</strong> — typically your employees and contractors. Data: name, email, OAuth
            identifiers, role, IP/UA of authentication.
          </li>
        </UL>
      </Section>

      <Section title="Subprocessors">
        <P>We engage the following subprocessors:</P>
        <UL>
          <li>
            <strong>Cloud + Postgres</strong> — your selected region. Used to host the API and store template/send data.
          </li>
          <li>
            <strong>Stripe, Inc.</strong> — payment processing.
          </li>
          <li>
            <strong>Email provider(s) you select</strong> — Resend, SendGrid, Postmark, Mailgun, Amazon SES, or your
            own SMTP. Required to actually deliver email.
          </li>
          <li>
            <strong>n8n GmbH</strong> — only when your workspace enables advanced automations.
          </li>
        </UL>
        <P>
          We give 30 days' notice (via email to workspace owners) before adding or replacing a subprocessor.
          You may object in writing within that window; if we cannot resolve the objection, you may terminate the
          affected services without penalty.
        </P>
      </Section>

      <Section title="Security measures">
        <UL>
          <li>Encryption in transit (TLS 1.2+) and at rest (AES-256-GCM via per-tenant data keys).</li>
          <li>Postgres Row-Level Security on every tenant-scoped table; tenant context propagated via AsyncLocalStorage.</li>
          <li>Argon2id password hashing for accounts that use passwords.</li>
          <li>Audit log of all admin and security-relevant actions, retained for 24 months.</li>
          <li>Role-based access control: owner, admin, editor, viewer.</li>
          <li>Vulnerability scanning + dependency review on every pull request.</li>
        </UL>
      </Section>

      <Section title="International transfers">
        <P>
          For transfers of personal data out of the EEA/UK/Switzerland, we rely on the European Commission's Standard
          Contractual Clauses (Module 2) plus the UK Addendum where applicable. We will execute the SCCs with your
          legal entity on request.
        </P>
      </Section>

      <Section title="Personal data breaches">
        <P>
          We will notify workspace owners without undue delay, and in any event within 72 hours, after becoming aware
          of a personal data breach affecting their data, with the information required by Article 33(3) GDPR.
        </P>
      </Section>

      <Section title="Audit rights">
        <P>
          You may request our most recent SOC 2 / ISO 27001 / penetration test summary once per 12 months. For audits
          beyond that, you may engage a mutually agreed independent auditor at your cost, with at least 30 days' notice
          and during business hours, subject to confidentiality.
        </P>
      </Section>

      <Section title="Return / deletion of data">
        <P>
          On termination of the workspace, we return or delete (your choice) all personal data within 30 days, save
          to the extent applicable law requires retention. Workspace owners can also use the in-product export and
          delete tools at any time without contacting us.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          DPA execution requests: <a href="mailto:legal@emailforge.app" style={{ color: 'var(--flame-2)' }}>legal@emailforge.app</a>.
          See <Link to="/privacy" style={{ color: 'var(--flame-2)' }}>Privacy Policy</Link> for general processing
          information.
        </P>
      </Section>
    </LegalPage>
  );
}
